import axios from 'axios';

const ENV_URL = process.env.REACT_APP_BACKEND_URL || '';
const isCustomDomain = typeof window !== 'undefined' && !window.location.hostname.includes('emergentagent.com') && !window.location.hostname.includes('localhost');
const API_URL = isCustomDomain ? '' : ENV_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' }
});

/**
 * Short-lived in-memory cache for GET responses on the storefront hot paths.
 * Dramatically speeds up repeat navigations (home ↔ shop ↔ product pages)
 * because we don't wait for the network on cache-hit. TTL = 60s.
 * Cached paths are read-only public ones; mutations don't invalidate because
 * admin writes are on a separate data plane from storefront reads.
 */
const _cache = new Map();
const CACHE_TTL_MS = 60_000;
const CACHED_PATHS = /^\/(?:products|products\/home|products\/admin|categories|sliders|announcement|settings|shipping\/rates|pages|pages\/[^/]+|search)(?:\?|$)/;

api.interceptors.request.use((cfg) => {
    if (cfg.method !== 'get' || cfg.__nocache) return cfg;
    const full = cfg.url + (cfg.params ? '?' + new URLSearchParams(cfg.params).toString() : '');
    if (!CACHED_PATHS.test(full)) return cfg;
    const entry = _cache.get(full);
    if (entry && (Date.now() - entry.t) < CACHE_TTL_MS) {
        cfg.adapter = async () => ({
            data: entry.data, status: 200, statusText: 'OK (memory-cache)',
            headers: {}, config: cfg, request: null,
        });
    } else {
        cfg.__cache_key = full;
    }
    return cfg;
});
api.interceptors.response.use((res) => {
    const key = res.config?.__cache_key;
    if (key && res.status === 200) _cache.set(key, { t: Date.now(), data: res.data });
    // Any non-GET response invalidates the entire short-lived cache so admins/staff
    // never see stale lists after a create/update/delete (e.g. deleted products
    // were lingering on the admin Products page until TTL expired).
    if (res.config && res.config.method && res.config.method.toLowerCase() !== 'get') {
        _cache.clear();
    }
    return res;
});

export function clearApiCache() { _cache.clear(); }

export default api;
export { API_URL };

export function formatPrice(price) {
    if (!price && price !== 0) return '';
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(price);
}

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
    if (detail?.msg) return detail.msg;
    return String(detail);
}

export function getGuestId() {
    let gid = localStorage.getItem('bbc_guest_id');
    if (!gid) {
        gid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('bbc_guest_id', gid);
    }
    return gid;
}
